import React, { useEffect, useState, useMemo } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/styles";
import { useForm, Controller } from "react-hook-form";
import { Button, Grid, Typography } from "@material-ui/core";
import { yupResolver } from "@hookform/resolvers/yup";

import { COLORS } from "../../constants/styles";
import { TITLE_BUTTONS } from "../../constants/titles";
import { REQUIRED_FIELDS } from "../../constants/data";
import { GET_STATES_BY_COUNTRY_ISO } from "../../constants/endpoints";
import {
  getUserTimezone,
  ensureTimezoneOption,
  getTimezoneOptionsFor,
  withTimezoneSelectable,
} from "../../constants/timezones";

import { schema } from "../../utils/getSchemaVenueForm";
import { fetchTimezones } from "../../utils/timezoneApi";
import { getList } from "../../dataProvider/API";

import TextFormField from "../TextFormField";
import CountrySelect from "../FormFields/CountrySelect";
import AutocompleteField from "../FormFields/AutocompleteField";

const useStyles = makeStyles({
  submit: {
    width: "80px",
    height: "30px",
    backgroundColor: COLORS.BACKGROUND_GREEN,
    marginRight: "10px",
    "& p": {
      color: COLORS.TEXT_WHITE,
      fontSize: "0.8rem",
    },
  },
  cansel: {
    width: "80px",
    height: "30px",
    "& p": {
      fontSize: "0.8rem",
    },
  },
  fieldForm: {
    width: "350px",
  },
  timezoneHint: {
    fontSize: "12px",
    color: "#667085",
    marginTop: "4px",
  },
  timezoneLink: {
    color: COLORS.BACKGROUND_GREEN,
    cursor: "pointer",
    textDecoration: "underline",
  },
});

const FieldOfCreate = ({
  dataForForms,
  handleClose,
  defaultValue,
  onSubmit,
}) => {
  const classes = useStyles();

  const userTimezone = getUserTimezone();
  const defaultTimezone =
    defaultValue?.timezone &&
      typeof defaultValue.timezone === "string" &&
      defaultValue.timezone.trim() !== ""
      ? defaultValue.timezone
      : userTimezone;

  const { control, handleSubmit, watch, setValue } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      ...defaultValue,
      timezone: defaultTimezone,
    },
  });

  const [states, setStates] = useState([]);

  const phone = watch("mobilePhone") || defaultValue.mobilePhone;
  const website = watch("website") || defaultValue.website;
  const country = watch("country") || defaultValue.country;
  const state = watch("state") || defaultValue.state;
  const timezone = watch("timezone") || defaultTimezone;

  const statesOptions = states?.map(({ name, abbreviation }) => ({
    id: abbreviation,
    label: name,
  }));
  const stateValue = state
    ? statesOptions.find((item) => item?.id === state)
    : state;

  /**
   * Resolve the option WITHOUT ever swapping in a different zone.
   *
   * This previously fell back to the browser's zone whenever the stored id wasn't in the
   * curated list. AutocompleteField seeds its state from this value and fires onChange on
   * mount, so simply opening a venue stored as e.g. America/Detroit and pressing Save
   * wrote the admin's own timezone over it. ensureTimezoneOption synthesises an option for
   * any id instead, so the stored value round-trips untouched. The browser zone is only
   * used when there is genuinely no value yet (new records).
   */
  const timezoneValue = useMemo(() => {
    const tz = timezone || defaultTimezone;
    return ensureTimezoneOption(tz) || ensureTimezoneOption(userTimezone);
  }, [timezone, defaultTimezone, userTimezone]);

  /**
   * Suggested timezones for this address, from GET /restapi/timezones?country&state.
   *
   * A narrowed list is a SUGGESTION, never a validity rule — country/state narrowing
   * returns a single zone even for multi-zone states (MI -> America/Detroit, though the UP
   * is Central), so we never auto-select from it. The user picks, and "show all" stays
   * reachable. `null` means "not loaded / not applicable" and falls back to the full list.
   */
  const [suggested, setSuggested] = useState(null);
  const [isNarrowed, setIsNarrowed] = useState(false);
  const [showAllTimezones, setShowAllTimezones] = useState(false);

  useEffect(() => {
    // Nothing to narrow by yet — show everything.
    if (!country) {
      setSuggested(null);
      setIsNarrowed(false);
      return undefined;
    }

    let active = true;

    fetchTimezones({ country, state }).then(({ options, narrowed }) => {
      if (!active) return;
      setSuggested(options);
      setIsNarrowed(narrowed);
    });

    return () => {
      active = false;
    };
  }, [country, state]);

  const currentTimezone = timezone || defaultTimezone;

  // Guarantee the current selection is present, whichever list is showing.
  const timezoneOptions = useMemo(() => {
    const base =
      !showAllTimezones && suggested?.length
        ? suggested
        : getTimezoneOptionsFor(currentTimezone);

    return withTimezoneSelectable(base, currentTimezone);
  }, [showAllTimezones, suggested, currentTimezone]);

  // Only offer the escape hatch when a narrowed list is actually hiding options.
  const canShowAllTimezones = isNarrowed && !showAllTimezones;

  const onChangeCountry = () => {
    setValue("state", null);
    setStates([]);
  };

  // Called with (name, value); only the name is needed to decide on a side effect.
  const onChange = (name) => {
    switch (name) {
      case "country":
        return onChangeCountry();
      default:
        return;
    }
  };

  useEffect(() => {
    if (phone === "") setValue("mobilePhone", null);
    if (website === "") setValue("website", null);
  }, [phone, website, setValue]);

  useEffect(() => {
    if (country)
      getList(GET_STATES_BY_COUNTRY_ISO(country)).then((res) => setStates(res));
  }, [country]);

  return (
    <React.Fragment>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid
          container
          spacing={2}
          direction="column"
          justifyContent="center"
          alignItems="center"
        >
          {dataForForms
            .filter(([name]) =>
              name === "state" ? !!statesOptions?.length : true,
            )
            .map((elem) => {
              const [name, title] = elem;

              return (
                <Grid item key={name} className={classes.fieldForm}>
                  <Controller
                    render={({ field, fieldState }) => {
                      const props = {
                        field: {
                          ...field,
                          required: REQUIRED_FIELDS.includes(name),
                          onChange: (value) => {
                            field.onChange(value);
                            onChange(name, value);
                          },
                        },
                        label: title,
                        valueNormalizer: (value) => {
                          if (["email", "backupEmail"].includes(name))
                            return value?.toLowerCase()?.trim();

                          return value;
                        },
                      };

                      const onChangeState = (value) => {
                        field.onChange(value?.id || "");
                      };

                      const onChangeTimezone = (value) => {
                        field.onChange(value?.id || "");
                      };

                      switch (name) {
                        case "country":
                          return <CountrySelect {...props} />;
                        case "state":
                          return (
                            <AutocompleteField
                              {...props}
                              size="small"
                              field={{
                                ...props.field,
                                value: stateValue,
                                onChange: onChangeState,
                              }}
                              data={statesOptions}
                            />
                          );
                        case "timezone":
                          return (
                            <React.Fragment>
                              <AutocompleteField
                                {...props}
                                size="small"
                                field={{
                                  ...props.field,
                                  value: timezoneValue,
                                  onChange: onChangeTimezone,
                                }}
                                data={timezoneOptions}
                              />

                              {/* Multi-zone states return a single suggestion, so the
                                  full list must always stay one click away. */}
                              {canShowAllTimezones ? (
                                <Typography className={classes.timezoneHint}>
                                  Suggested for {state || country}.{" "}
                                  <span
                                    role="button"
                                    tabIndex={0}
                                    className={classes.timezoneLink}
                                    onClick={() => setShowAllTimezones(true)}
                                    onKeyDown={(e) => {
                                      if (e.key === "Enter" || e.key === " ") {
                                        setShowAllTimezones(true);
                                      }
                                    }}
                                  >
                                    Show all timezones
                                  </span>
                                </Typography>
                              ) : null}
                            </React.Fragment>
                          );
                        default:
                          return (
                            <TextFormField {...props} fieldState={fieldState} />
                          );
                      }
                    }}
                    control={control}
                    name={name}
                    defaultValue={
                      name === "timezone"
                        ? defaultTimezone
                        : defaultValue[name] || null
                    }
                  />
                </Grid>
              );
            })}

          <Grid item>
            <Button
              className={classes.submit}
              type="submit"
              variant="contained"
            >
              <Typography>{TITLE_BUTTONS.FINISH}</Typography>
            </Button>
            <Button
              className={classes.cansel}
              variant="outlined"
              onClick={handleClose}
            >
              <Typography>{TITLE_BUTTONS.CANCEL}</Typography>
            </Button>
          </Grid>
        </Grid>
      </form>
    </React.Fragment>
  );
};

FieldOfCreate.propTypes = {
  dataForForms: PropTypes.array,
  handleClose: PropTypes.func,
  onSubmit: PropTypes.func,
  defaultValue: PropTypes.object,
};

export default FieldOfCreate;
